/**
 * Generation pricing.
 *
 * Image + video: provider COGS ×2 when the invoice is known (OpenRouter
 * `usage.cost`, Seedance completion tokens × list $/M), else quote tables.
 * Round **up** to next TT$0.50. Quotes stay estimates; the ledger true-ups.
 * Text: OpenRouter/Ark invoice ×2 when present, else list × measured tokens.
 * Text sell rounding — sub-cent → TT$0.01, otherwise round **up** to next
 * TT$0.05 (0.04→0.05, 0.08→0.10).
 * FX: US$1 = TT$10. Ledger: TT$0.50 per credit (fractional credits OK for nickels).
 * No platform fee on top of the 2× markup.
 *
 * Seedance 2.5 list: 480p/720p $10.70/M ($6.40/M with video input).
 * 1080p $11.70/M ($7.00/M with video input). 4K is not a 2.5 tier.
 * tokens ≈ (height × width × 24fps × seconds) / 1024. Audio included in rate.
 * Resolutions 480p/720p/1080p; max 30s.
 *
 * Seedance 2.0 list (no video input): 480p/720p $7/M, 1080p $7.7/M, 4k $4/M.
 * With video input: 480p/720p $4.30/M, 1080p $4.70/M, 4k $2.40/M.
 * Resolutions 480p/720p/1080p/4k; max 15s.
 *
 * GPT Image 2 via OpenRouter (square 1024): low $0.011 / medium $0.042 /
 * high $0.167 COGS per image (token math: $5/M text-in, $30/M image-out).
 * Wide/portrait scale with pixels. Studio 1K/2K/4K tiers are UX labels now —
 * cost follows quality. First reference read-in absorbed; each additional
 * reference +$0.003 COGS (then ×2 markup).
 *
 * Text — Glimmer fast writing ($0.30/$1.20); Muse Spark 1.3 ($1.25/$4.25);
 * Muse Spark 1.3 Contributor ($0.10/$0.20).
 * Seed 2.1 Turbo / 2.0 Pro / Lite / Mini — legacy Ark tiers.
 * Measured-token charges use the model that ran. Customer = 2× COGS.
 */

export const CREDIT_PRICE_TTD = 0.5;
export const USD_TO_TTD = 10;
export const MIN_GROSS_MARGIN = 0.35;

/** GPT Image 2 quality knob — drives OpenRouter COGS (not a legacy label). */
export type ImageQuality = "low" | "medium" | "high";

import { mediaModelById } from "./mediaModels";

/**
 * GPT Image 2 via OpenRouter — USD per output image by quality tier.
 * COGS (square 1024×1024, token math at $5/M text-in / $30/M image-out):
 * low ≈ 272 out-tokens ≈ $0.011; medium ≈ 1056 ≈ $0.042; high ≈ 4160 ≈ $0.167.
 * Wide/portrait outputs scale with pixels; the table below uses square list
 * prices and the endpoint `usage.cost` as the audited fallback.
 */
const GPT_IMAGE_2_USD_BY_QUALITY: Record<ImageQuality, number> = {
  low: 0.011,
  medium: 0.042,
  high: 0.167,
};

/** @deprecated Seedream retired — kept for call-site compat while refs drain. */
const SEEDREAM_USD_BY_TIER: Record<"1K" | "2K" | "4K", number> = {
  "1K": 0.011,
  "2K": 0.042,
  "4K": 0.167,
};

/** GPT Image 2 — first reference read-in is token-billed; surcharge stays 1 credit floor. */
export const SEEDREAM_USD_EXTRA_REFERENCE = 0.003;

/** @deprecated Prefer GPT_IMAGE_2_USD_BY_QUALITY — kept for any direct reads. */
const IMAGE_MODEL_USD: Record<
  "1K" | "2K" | "4K",
  Record<ImageQuality, { square: number; wide: number }>
> = {
  "1K": {
    low: { square: 0.011, wide: 0.011 },
    medium: { square: 0.042, wide: 0.042 },
    high: { square: 0.167, wide: 0.167 },
  },
  "2K": {
    low: { square: 0.011, wide: 0.011 },
    medium: { square: 0.042, wide: 0.042 },
    high: { square: 0.167, wide: 0.167 },
  },
  "4K": {
    low: { square: 0.167, wide: 0.167 },
    medium: { square: 0.167, wide: 0.167 },
    high: { square: 0.167, wide: 0.167 },
  },
};

/** Legacy base credits (pre-2× model pricing) — kept for billing display fallbacks. */
export const IMAGE_CREDITS_BY_RESOLUTION: Record<string, number> = {
  "1K": 1,
  "2K": 2,
  "4K": 7,
};

/**
 * Display helper: customer credits for **one billed** extra Seedream reference
 * (after the free first ref), at 2× markup rounded like image jobs.
 */
export const IMAGE_REFERENCE_SURCHARGE = Math.max(
  1,
  Math.round(
    (Math.ceil((SEEDREAM_USD_EXTRA_REFERENCE * USD_TO_TTD * 2) / CREDIT_PRICE_TTD) *
      CREDIT_PRICE_TTD) /
      CREDIT_PRICE_TTD,
  ),
);

/** Seedance 2.5 — USD per million video tokens. 480p/720p share a rate. */
export const SEEDANCE_USD_PER_M_TOKENS_NO_VIDEO = 10.7;
export const SEEDANCE_USD_PER_M_TOKENS_WITH_VIDEO = 6.4;
/** Seedance 2.5 1080p list (ModelArk, after the Sep 2026 promo). */
export const SEEDANCE_25_USD_PER_M_TOKENS_1080P = 11.7;
export const SEEDANCE_25_USD_PER_M_TOKENS_1080P_WITH_VIDEO = 7;
/** Seedance 2.0 no-video rates by resolution tier. */
export const SEEDANCE_20_USD_PER_M_TOKENS_NO_VIDEO = 7;
export const SEEDANCE_20_USD_PER_M_TOKENS_1080P = 7.7;
export const SEEDANCE_20_USD_PER_M_TOKENS_4K = 4;
/** Seedance 2.0 with-video rates by resolution tier. */
export const SEEDANCE_20_USD_PER_M_TOKENS_WITH_VIDEO = 4.3;
export const SEEDANCE_20_USD_PER_M_TOKENS_1080P_WITH_VIDEO = 4.7;
export const SEEDANCE_20_USD_PER_M_TOKENS_4K_WITH_VIDEO = 2.4;
export const SEEDANCE_FPS = 24;
export const SEEDANCE_25_MAX_DURATION_SECONDS = 30;
export const SEEDANCE_20_MAX_DURATION_SECONDS = 15;

/** @deprecated Omni Flash removed from Studio. */
export const OMNI_FLASH_USD_PER_SECOND = 0.1;
export const OMNI_FLASH_MAX_DURATION_SECONDS = 10;

/** @deprecated Kling removed from Studio. */
export const KLING_PRO_USD_PER_SECOND_SILENT = 0.224;
export const KLING_PRO_USD_PER_SECOND_AUDIO = 0.336;

export type VideoPricingModel = "seedance-2.5" | "seedance-2.0";

const VIDEO_RESOLUTION_WH: Record<string, { width: number; height: number }> = {
  "854x480": { width: 854, height: 480 },
  "864x480": { width: 854, height: 480 },
  "480p": { width: 854, height: 480 },
  "1280x720": { width: 1280, height: 720 },
  "720p": { width: 1280, height: 720 },
  "1920x1080": { width: 1920, height: 1080 },
  "1080p": { width: 1920, height: 1080 },
  "3840x2160": { width: 3840, height: 2160 },
  "4k": { width: 3840, height: 2160 },
  "2160p": { width: 3840, height: 2160 },
};

/** @deprecated Fixed block tables replaced by 2× gateway COGS — use videoCreditCost. */
export const SEEDANCE_VIDEO_BASE_CREDITS_PER_BLOCK: Record<string, number> = {
  "854x480": 14,
  "1280x720": 31,
  "1920x1080": 69,
};

/** @deprecated use SEEDANCE_VIDEO_BASE_CREDITS_PER_BLOCK */
export const VIDEO_BASE_CREDITS_PER_BLOCK = SEEDANCE_VIDEO_BASE_CREDITS_PER_BLOCK;

/** @deprecated Fixed block tables replaced by 2× gateway COGS — use videoCreditCost. */
export const KLING_VIDEO_BASE_CREDITS_PER_BLOCK: Record<string, number> = {
  "854x480": 45,
  "1280x720": 45,
  "1920x1080": 45,
};

/** @deprecated Gateway includes audio in Seedance; Kling uses rate swap instead. */
export const KLING_VIDEO_AUDIO_SURCHARGE_PER_BLOCK = 0;
/** @deprecated */
export const VIDEO_AUDIO_SURCHARGE_PER_BLOCK = 0;
/** @deprecated Image refs do not change Seedance gateway token rate. */
export const VIDEO_IMAGE_REFERENCE_SURCHARGE_PER_BLOCK = 0;
/** @deprecated */
export const VIDEO_NON_VIDEO_REFERENCE_SURCHARGE_720 = 0;
/** @deprecated */
export const VIDEO_NON_VIDEO_REFERENCE_SURCHARGE_1080 = 0;
/**
 * @deprecated Video refs switch Seedance to the cheaper $4.3/M token rate
 * (gateway bill can still include input tokens; customer price uses output tokens at that rate).
 */
export const VIDEO_VIDEO_REFERENCE_SURCHARGE_PER_BLOCK = 0;

/**
 * @deprecated Fixed platform overhead removed — image/video/text use 2× COGS only.
 * Kept for billing UI field compatibility.
 */
export const PLATFORM_OVERHEAD_CREDITS_MEDIA = 0;
/** @deprecated Text uses 2× Seed Lite COGS — see textCreditCost. */
export const PLATFORM_OVERHEAD_CREDITS_TEXT = 0;

/**
 * Fast writing (DM Improve / composer Enhance / text-only polish) —
 * Glimmer 30B via OpenRouter ($0.30/M in / $1.10/M out).
 * Multimodal writing + Assistance + agent — actual Muse Spark 1.3 rates via
 * OpenRouter ($1.25/M in / $4.25/M out, +$1.25/M image/audio in).
 * Seed 2.1 Turbo — legacy Agent / Assistance default (Ark).
 * Seed 2.0 Pro / Lite / Mini — legacy optional tiers (Ark).
 */
export type TextPricingModel =
  | "turbo"
  | "pro"
  | "lite"
  | "mini"
  | "glimmer"
  | "flash"
  | "spark"
  | "spark-contributor"
  | "gemini"
  | "luna"
  | "union"
  | "glm"
  | "glm-flash"
  | "mercury"
  | "sol"
  | "luna56"
  | "haiku55"
  | "terra"
  | "deepseek-flash"
  | "qwen-flash"
  | "gemini-premium"
  | "gemini-lite"
  | "gemini-mini"
  | "gpt5-mini"
  /** Unknown / unlisted models: the priciest known rate (never a cheaper default). */
  | "premium";

/** Seed 2.1 Turbo list (≈ Volcano CNY→USD; ≤256k). */
export const TEXT_TURBO_USD_PER_M_INPUT = 0.45;
export const TEXT_TURBO_USD_PER_M_OUTPUT = 2.25;
export const TEXT_TURBO_USD_PER_M_CACHE_READ = 0.09;
export const TEXT_TURBO_USD_PER_M_AUDIO_INPUT = TEXT_TURBO_USD_PER_M_INPUT;

/** Seed 2.0 Pro list (≤128k) — optional Pro tier. */
export const TEXT_USD_PER_M_INPUT = 0.5;
export const TEXT_USD_PER_M_OUTPUT = 3.0;
/** BytePlus ModelArk cache-hit input (Seed 2.0 Pro ≤128k). */
export const TEXT_USD_PER_M_CACHE_READ = 0.1;
/**
 * BytePlus ModelArk cache storage (USD / M tokens / hour).
 * When providers report cache_write_tokens without hours, bill 1h (min Context TTL).
 */
export const TEXT_USD_PER_M_CACHE_STORAGE = 0.008333;
/** Assumed storage hours when only write-token count is known (Context API min TTL). */
export const TEXT_CACHE_STORAGE_ASSUMED_HOURS = 1;
export const TEXT_USD_PER_M_AUDIO_INPUT = TEXT_USD_PER_M_INPUT;

export const TEXT_LITE_USD_PER_M_INPUT = 0.25;
export const TEXT_LITE_USD_PER_M_OUTPUT = 2.0;
export const TEXT_LITE_USD_PER_M_CACHE_READ = 0.05;
export const TEXT_LITE_USD_PER_M_AUDIO_INPUT = TEXT_LITE_USD_PER_M_INPUT;

export const TEXT_MINI_USD_PER_M_INPUT = 0.1;
export const TEXT_MINI_USD_PER_M_OUTPUT = 0.4;
export const TEXT_MINI_USD_PER_M_CACHE_READ = 0.02;
export const TEXT_MINI_USD_PER_M_AUDIO_INPUT = TEXT_MINI_USD_PER_M_INPUT;

/** Muse Glimmer 30B via OpenRouter — fast writing utility tier. */
export const TEXT_GLIMMER_USD_PER_M_INPUT = 0.3;
export const TEXT_GLIMMER_USD_PER_M_OUTPUT = 1.2;
export const TEXT_GLIMMER_USD_PER_M_CACHE_READ = 0.04;
export const TEXT_GLIMMER_USD_PER_M_AUDIO_INPUT = TEXT_GLIMMER_USD_PER_M_INPUT;

/** Muse Spark 1.3 via OpenRouter — multimodal writing / agent tier ($1.25/M in / $4.25/M out). */
export const TEXT_FLASH_USD_PER_M_INPUT = 1.25;
export const TEXT_FLASH_USD_PER_M_OUTPUT = 4.25;
export const TEXT_FLASH_USD_PER_M_CACHE_READ = 0.15;
export const TEXT_FLASH_USD_PER_M_AUDIO_INPUT = TEXT_FLASH_USD_PER_M_INPUT;

/** meta/muse-spark-1.3-contributor list ($0.10/M in / $0.20/M out). */
export const TEXT_SPARK_CONTRIBUTOR_USD_PER_M_INPUT = 0.1;
export const TEXT_SPARK_CONTRIBUTOR_USD_PER_M_OUTPUT = 0.2;
export const TEXT_SPARK_CONTRIBUTOR_USD_PER_M_CACHE_READ = 0.002;
export const TEXT_SPARK_CONTRIBUTOR_USD_PER_M_AUDIO_INPUT = TEXT_SPARK_CONTRIBUTOR_USD_PER_M_INPUT;

/** @deprecated "flash" tier id bills actual Muse Spark rates (kept for ledger compat). */
export const TEXT_SPARK_USD_PER_M_INPUT = TEXT_FLASH_USD_PER_M_INPUT;
export const TEXT_SPARK_USD_PER_M_OUTPUT = TEXT_FLASH_USD_PER_M_OUTPUT;
export const TEXT_SPARK_USD_PER_M_CACHE_READ = TEXT_FLASH_USD_PER_M_CACHE_READ;
export const TEXT_SPARK_USD_PER_M_AUDIO_INPUT = TEXT_FLASH_USD_PER_M_AUDIO_INPUT;

/**
 * Curated OpenRouter multimodal text catalog — per-model list rates.
 * Ledger tiers: gemini / luna / glm / glm-flash are billed per-model so the
 * Settings picker can show true cost. "flash" keeps Spark list rates and is
 * not folded into the gemini tier (Gemini bills its own list).
 */
export type TextCatalogEntry = {
  input: number;
  output: number;
  cacheRead: number;
  audioInput: number;
};

/** Gemini 3.8 Flash list via OpenRouter ($0.75/M in / $3.75/M out). */
export const TEXT_GEMINI_USD_PER_M_INPUT = 0.75;
export const TEXT_GEMINI_USD_PER_M_OUTPUT = 3.75;
export const TEXT_GEMINI_USD_PER_M_CACHE_READ = 0.075;
export const TEXT_GEMINI_USD_PER_M_AUDIO_INPUT = TEXT_GEMINI_USD_PER_M_INPUT;

/** GPT-6 Luna / Luna Pro list via OpenRouter ($0.10/M in / $0.50/M out, $0.01/M cache read). */
// 2026-10-29: Fast tier (openai/fast) = 2x OpenRouter standard list.
export const TEXT_LUNA_USD_PER_M_INPUT = 0.2;
export const TEXT_LUNA_USD_PER_M_OUTPUT = 1.0;
export const TEXT_LUNA_USD_PER_M_CACHE_READ = 0.02;
export const TEXT_LUNA_USD_PER_M_AUDIO_INPUT = TEXT_LUNA_USD_PER_M_INPUT;

/** Union Alpha via OpenRouter stealth preview — $0 / $0 while the window lasts. */
export const TEXT_UNION_USD_PER_M_INPUT = 0;
export const TEXT_UNION_USD_PER_M_OUTPUT = 0;
export const TEXT_UNION_USD_PER_M_CACHE_READ = 0;
export const TEXT_UNION_USD_PER_M_AUDIO_INPUT = TEXT_UNION_USD_PER_M_INPUT;

/** Z.ai GLM 5.3 list via OpenRouter ($1.40/M in / $4.40/M out, $0.26/M cache read). */
export const TEXT_GLM_USD_PER_M_INPUT = 1.4;
export const TEXT_GLM_USD_PER_M_OUTPUT = 4.4;
export const TEXT_GLM_USD_PER_M_CACHE_READ = 0.26;
export const TEXT_GLM_USD_PER_M_AUDIO_INPUT = TEXT_GLM_USD_PER_M_INPUT;

/** Z.ai GLM 5.3 Flash list via OpenRouter ($0.15/M in / $0.50/M out, $0.03/M cache read). */
export const TEXT_GLM_FLASH_USD_PER_M_INPUT = 0.15;
export const TEXT_GLM_FLASH_USD_PER_M_OUTPUT = 0.5;
export const TEXT_GLM_FLASH_USD_PER_M_CACHE_READ = 0.03;
export const TEXT_GLM_FLASH_USD_PER_M_AUDIO_INPUT = TEXT_GLM_FLASH_USD_PER_M_INPUT;

/** Inception Mercury 2.5 via OpenRouter ($0.04/M in / $0.15/M out, $0.004/M cache read). */
export const TEXT_MERCURY_USD_PER_M_INPUT = 0.04;
export const TEXT_MERCURY_USD_PER_M_OUTPUT = 0.15;
export const TEXT_MERCURY_USD_PER_M_CACHE_READ = 0.004;
export const TEXT_MERCURY_USD_PER_M_AUDIO_INPUT = TEXT_MERCURY_USD_PER_M_INPUT;

/** GPT-6 Sol Pro via OpenRouter ($2.00/M in / $10.00/M out, $0.20/M cache read). */
// 2026-10-29: Fast tier (openai/fast) = 2x OpenRouter standard list.
export const TEXT_SOL_PRO_USD_PER_M_INPUT = 4.0;
export const TEXT_SOL_PRO_USD_PER_M_OUTPUT = 20.0;
export const TEXT_SOL_PRO_USD_PER_M_CACHE_READ = 0.4;
export const TEXT_SOL_PRO_USD_PER_M_AUDIO_INPUT = TEXT_SOL_PRO_USD_PER_M_INPUT;

/** GPT-6.1 Sol Pro on OpenAI's Fast tier via OpenRouter (endpoint openai/fast, 2026-10-29): $4/M in, $20/M out, $0.20/M cache read. Ad Engine default. */
export const TEXT_SOL61_PRO_FAST_USD_PER_M_INPUT = 4.0;
export const TEXT_SOL61_PRO_FAST_USD_PER_M_OUTPUT = 20.0;
export const TEXT_SOL61_PRO_FAST_USD_PER_M_CACHE_READ = 0.2;

/**
 * Anthropic Claude Haiku 5.5 — Luna helper lane (2026-10-10).
 * Anthropic list (platform.claude.com pricing, checked 2026-10-10), same on
 * OpenRouter anthropic/claude-haiku-5.5: prompts up to 100K tokens
 * $0.10/M in, $0.50/M out, $0.01/M cache read. Prompts over 100K tokens
 * (input + cache read + cache write) bill the whole request at
 * $0.50/M in, $2.50/M out, $0.05/M cache read — see textProviderCostUsd.
 */
export const TEXT_HAIKU55_USD_PER_M_INPUT = 0.1;
export const TEXT_HAIKU55_USD_PER_M_OUTPUT = 0.5;
export const TEXT_HAIKU55_USD_PER_M_CACHE_READ = 0.01;
export const TEXT_HAIKU55_USD_PER_M_AUDIO_INPUT = TEXT_HAIKU55_USD_PER_M_INPUT;
export const TEXT_HAIKU55_LONG_PROMPT_TOKENS = 100_000;
export const TEXT_HAIKU55_LONG_USD_PER_M_INPUT = 0.5;
export const TEXT_HAIKU55_LONG_USD_PER_M_OUTPUT = 2.5;
export const TEXT_HAIKU55_LONG_USD_PER_M_CACHE_READ = 0.05;

type CatalogTextTier =
  | "gemini"
  | "luna"
  | "union"
  | "glm"
  | "glm-flash"
  | "mercury"
  | "sol"
  | "luna56"
  | "haiku55"
  | "terra"
  | "deepseek-flash"
  | "qwen-flash"
  | "gemini-premium"
  | "gemini-lite"
  | "gemini-mini"
  | "gpt5-mini"
  | "premium";

/** Per-model OpenRouter list rates (USD / M tokens), checked live 2026-09-28. */
function orRate(input: number, output: number, cacheRead: number): TextCatalogEntry {
  return { input, output, cacheRead, audioInput: input };
}

const TEXT_CATALOG_RATES: Record<CatalogTextTier, TextCatalogEntry> = {
  /** openai/gpt-5.6-luna, gpt-5.6-luna-pro */
  luna56: orRate(0.2, 1.2, 0.02),
  /** anthropic/claude-haiku-5.5 (≤100K prompt; long prompts: textProviderCostUsd) */
  haiku55: {
    input: TEXT_HAIKU55_USD_PER_M_INPUT,
    output: TEXT_HAIKU55_USD_PER_M_OUTPUT,
    cacheRead: TEXT_HAIKU55_USD_PER_M_CACHE_READ,
    audioInput: TEXT_HAIKU55_USD_PER_M_AUDIO_INPUT,
  },
  /** openai/gpt-6.1-terra(-pro) on openai/fast, OpenRouter 2026-10-29: $2/M in, $10/M out, $0.10/M cache read. */
  terra: orRate(2.0, 10.0, 0.1),
  /** deepseek/deepseek-v4.1-flash */
  "deepseek-flash": orRate(0.035, 0.29, 0.001),
  /** qwen/qwen3.8-flash */
  "qwen-flash": orRate(0.15, 0.47, 0.016),
  /** google/gemini-3.5-flash */
  "gemini-premium": orRate(1.5, 9.0, 0.15),
  /** google/gemini-3.5-flash-lite, google/gemini-2.5-flash */
  "gemini-lite": orRate(0.3, 2.5, 0.03),
  /** google/gemini-2.5-flash-lite */
  "gemini-mini": orRate(0.1, 0.4, 0.01),
  /**
   * openai/gpt-5-mini. OpenRouter models API (GET /api/v1/models, checked
   * 2026-10-14): $0.25/M in, $2.00/M out, $0.025/M cache read, no long-prompt
   * override. Matches OpenAI list (openai.com/api/pricing).
   */
  "gpt5-mini": orRate(0.25, 2.0, 0.025),
  /** Unknown models: priciest known text rate (anthropic/claude-sonnet-4.5). */
  premium: orRate(3.0, 15.0, 0.3),
  mercury: {
    input: TEXT_MERCURY_USD_PER_M_INPUT,
    output: TEXT_MERCURY_USD_PER_M_OUTPUT,
    cacheRead: TEXT_MERCURY_USD_PER_M_CACHE_READ,
    audioInput: TEXT_MERCURY_USD_PER_M_AUDIO_INPUT,
  },
  sol: {
    input: TEXT_SOL_PRO_USD_PER_M_INPUT,
    output: TEXT_SOL_PRO_USD_PER_M_OUTPUT,
    cacheRead: TEXT_SOL_PRO_USD_PER_M_CACHE_READ,
    audioInput: TEXT_SOL_PRO_USD_PER_M_AUDIO_INPUT,
  },
  gemini: {
    input: TEXT_GEMINI_USD_PER_M_INPUT,
    output: TEXT_GEMINI_USD_PER_M_OUTPUT,
    cacheRead: TEXT_GEMINI_USD_PER_M_CACHE_READ,
    audioInput: TEXT_GEMINI_USD_PER_M_AUDIO_INPUT,
  },
  luna: {
    input: TEXT_LUNA_USD_PER_M_INPUT,
    output: TEXT_LUNA_USD_PER_M_OUTPUT,
    cacheRead: TEXT_LUNA_USD_PER_M_CACHE_READ,
    audioInput: TEXT_LUNA_USD_PER_M_AUDIO_INPUT,
  },
  union: {
    input: TEXT_UNION_USD_PER_M_INPUT,
    output: TEXT_UNION_USD_PER_M_OUTPUT,
    cacheRead: TEXT_UNION_USD_PER_M_CACHE_READ,
    audioInput: TEXT_UNION_USD_PER_M_AUDIO_INPUT,
  },
  glm: {
    input: TEXT_GLM_USD_PER_M_INPUT,
    output: TEXT_GLM_USD_PER_M_OUTPUT,
    cacheRead: TEXT_GLM_USD_PER_M_CACHE_READ,
    audioInput: TEXT_GLM_USD_PER_M_AUDIO_INPUT,
  },
  "glm-flash": {
    input: TEXT_GLM_FLASH_USD_PER_M_INPUT,
    output: TEXT_GLM_FLASH_USD_PER_M_OUTPUT,
    cacheRead: TEXT_GLM_FLASH_USD_PER_M_CACHE_READ,
    audioInput: TEXT_GLM_FLASH_USD_PER_M_AUDIO_INPUT,
  },
};

/** True when a text tier bills per-model catalog rates (not a legacy Ark tier). */
export function isCatalogTextTier(model: TextPricingModel): model is CatalogTextTier {
  return (
    model === "gemini" ||
    model === "luna" ||
    model === "union" ||
    model === "glm" ||
    model === "glm-flash" ||
    model === "mercury" ||
    model === "sol" ||
    model === "luna56" ||
    model === "haiku55" ||
    model === "terra" ||
    model === "deepseek-flash" ||
    model === "qwen-flash" ||
    model === "gemini-premium" ||
    model === "gemini-lite" ||
    model === "gemini-mini" ||
    model === "gpt5-mini" ||
    model === "premium"
  );
}

/** Per-model COGS entry for a catalog tier (null for legacy tiers). */
export function textCatalogRates(model: TextPricingModel): TextCatalogEntry | null {
  if (model === "gemini") return TEXT_CATALOG_RATES.gemini;
  if (model === "luna") return TEXT_CATALOG_RATES.luna;
  if (model === "union") return TEXT_CATALOG_RATES.union;
  if (model === "glm") return TEXT_CATALOG_RATES.glm;
  if (model === "glm-flash") return TEXT_CATALOG_RATES["glm-flash"];
  if (model === "mercury") return TEXT_CATALOG_RATES.mercury;
  if (model === "sol") return TEXT_CATALOG_RATES.sol;
  if (isCatalogTextTier(model)) return TEXT_CATALOG_RATES[model];
  return null;
}

/**
 * Typical Assistance / script turn on Seed 2.0 Pro
 * (estimate shape for UI quotes; ledger uses measured tokens).
 */
export const TEXT_BASE_INPUT_TOKENS = 2_000;
export const TEXT_BASE_OUTPUT_TOKENS = 600;

/** Approximate multimodal input tokens added per reference. */
export const TEXT_IMAGE_REF_INPUT_TOKENS = 1_200;
export const TEXT_VIDEO_REF_INPUT_TOKENS = 10_000;
export const TEXT_AUDIO_REF_INPUT_TOKENS = 5_000;

/**
 * Text floor: TT$0.01. Sub-cent COGS×2 bumps here; larger amounts round up to nickels.
 */
export const TEXT_MIN_SELL_TTD = 0.01;
/** Text sell step after the 1¢ floor — round up to next TT$0.05. */
export const TEXT_SELL_STEP_TTD = 0.05;

/** @deprecated Prefer textCreditCost() — legacy flat base for display fallbacks. */
export const TEXT_GENERATION_BASE_CREDITS = TEXT_MIN_SELL_TTD / CREDIT_PRICE_TTD;
/** @deprecated Reference media priced via token estimate in textCreditCost. */
export const TEXT_IMAGE_REFERENCE_CREDITS = 0;
/** @deprecated */
export const TEXT_AUDIO_REFERENCE_CREDITS = 0;
/** @deprecated */
export const TEXT_VIDEO_REFERENCE_CREDITS = 0;

export function normalizeImageResolutionLabel(
  resolution: string | undefined,
): keyof typeof SEEDREAM_USD_BY_TIER {
  const upper = (resolution ?? "2K").toUpperCase();
  if (upper === "1K" || upper === "2K" || upper === "4K") {
    return upper;
  }
  return "2K";
}

export function normalizeImageQuality(
  quality: string | undefined,
): ImageQuality {
  const lower = (quality ?? "medium").toLowerCase();
  if (lower === "low" || lower === "medium" || lower === "high") {
    return lower;
  }
  return "medium";
}

/** Round up to the next TT$0.50 (1.20→1.50, 1.80→2.00). */
export function roundUpToHalfTtd(ttd: number): number {
  return Math.ceil(ttd / CREDIT_PRICE_TTD) * CREDIT_PRICE_TTD;
}

/**
 * Text retail rounding (after COGS ×2):
 * - ≤0 or empty → TT$0.01 floor
 * - (0, 0.01) → TT$0.01 (sub-cent never under-bills below a cent)
 * - else round **up** to next TT$0.05 (integer cents — no float drift)
 */
export function roundTextSellTtd(ttd: number): number {
  if (!(ttd > 0)) return TEXT_MIN_SELL_TTD;
  if (ttd < TEXT_MIN_SELL_TTD) return TEXT_MIN_SELL_TTD;
  const cents = Math.max(1, Math.ceil(ttd * 100 - 1e-9));
  const nickels = Math.ceil(cents / 5) * 5;
  return nickels / 100;
}

function textRates(model: TextPricingModel = "glimmer"): {
  input: number;
  output: number;
  cacheRead: number;
  cacheStorage: number;
  audioInput: number;
} {
  const catalog = textCatalogRates(model);
  if (model === "union") {
    return {
      input: 0,
      output: 0,
      cacheRead: 0,
      cacheStorage: 0,
      audioInput: 0,
    };
  }
  if (catalog) {
    return {
      input: catalog.input,
      output: catalog.output,
      cacheRead: catalog.cacheRead,
      cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
      audioInput: catalog.audioInput,
    };
  }
  if (model === "glimmer") {
    return {
      input: TEXT_GLIMMER_USD_PER_M_INPUT,
      output: TEXT_GLIMMER_USD_PER_M_OUTPUT,
      cacheRead: TEXT_GLIMMER_USD_PER_M_CACHE_READ,
      cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
      audioInput: TEXT_GLIMMER_USD_PER_M_AUDIO_INPUT,
    };
  }
  if (model === "flash" || model === "spark") {
    return {
      input: TEXT_FLASH_USD_PER_M_INPUT,
      output: TEXT_FLASH_USD_PER_M_OUTPUT,
      cacheRead: TEXT_FLASH_USD_PER_M_CACHE_READ,
      cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
      audioInput: TEXT_FLASH_USD_PER_M_AUDIO_INPUT,
    };
  }
  if (model === "spark-contributor") {
    return {
      input: TEXT_SPARK_CONTRIBUTOR_USD_PER_M_INPUT,
      output: TEXT_SPARK_CONTRIBUTOR_USD_PER_M_OUTPUT,
      cacheRead: TEXT_SPARK_CONTRIBUTOR_USD_PER_M_CACHE_READ,
      cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
      audioInput: TEXT_SPARK_CONTRIBUTOR_USD_PER_M_AUDIO_INPUT,
    };
  }
  if (model === "mini") {
    return {
      input: TEXT_MINI_USD_PER_M_INPUT,
      output: TEXT_MINI_USD_PER_M_OUTPUT,
      cacheRead: TEXT_MINI_USD_PER_M_CACHE_READ,
      cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
      audioInput: TEXT_MINI_USD_PER_M_AUDIO_INPUT,
    };
  }
  if (model === "lite") {
    return {
      input: TEXT_LITE_USD_PER_M_INPUT,
      output: TEXT_LITE_USD_PER_M_OUTPUT,
      cacheRead: TEXT_LITE_USD_PER_M_CACHE_READ,
      cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
      audioInput: TEXT_LITE_USD_PER_M_AUDIO_INPUT,
    };
  }
  if (model === "pro") {
    return {
      input: TEXT_USD_PER_M_INPUT,
      output: TEXT_USD_PER_M_OUTPUT,
      cacheRead: TEXT_USD_PER_M_CACHE_READ,
      cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
      audioInput: TEXT_USD_PER_M_AUDIO_INPUT,
    };
  }
  return {
    input: TEXT_TURBO_USD_PER_M_INPUT,
    output: TEXT_TURBO_USD_PER_M_OUTPUT,
    cacheRead: TEXT_TURBO_USD_PER_M_CACHE_READ,
    cacheStorage: TEXT_USD_PER_M_CACHE_STORAGE,
    audioInput: TEXT_TURBO_USD_PER_M_AUDIO_INPUT,
  };
}

function gptImageReferenceImageCount(args: {
  referenceImageCount?: number;
  hasReferenceInput?: boolean;
}): number {
  if (args.referenceImageCount != null && Number.isFinite(args.referenceImageCount)) {
    return Math.max(0, Math.floor(args.referenceImageCount));
  }
  return args.hasReferenceInput ? 1 : 0;
}

export function estimateImageModelUsd(args: {
  resolution?: string;
  quality?: string;
  aspectRatio?: string;
  referenceImageCount?: number;
  hasReferenceInput?: boolean;
}): number {
  void args.resolution; // tiers are UX labels now — COGS follows quality
  void args.aspectRatio;
  const quality = normalizeImageQuality(args.quality);
  const refs = gptImageReferenceImageCount(args);
  const billedExtraRefs = Math.max(0, refs - 1); // first ref read-in absorbed
  return (
    GPT_IMAGE_2_USD_BY_QUALITY[quality] +
    billedExtraRefs * SEEDREAM_USD_EXTRA_REFERENCE
  );
}

/** Customer TT$ for an image = 2× model COGS, rounded up to TT$0.50. */
export function imageSellPriceTtd(args: {
  resolution?: string;
  quality?: string;
  aspectRatio?: string;
  referenceImageCount?: number;
  hasReferenceInput?: boolean;
}): number {
  const modelTtd = estimateImageModelUsd(args) * USD_TO_TTD;
  return roundUpToHalfTtd(modelTtd * 2);
}

export function imageCreditCost(args: {
  resolution?: string;
  quality?: string;
  aspectRatio?: string;
  hasReferenceInput?: boolean;
  referenceImageCount?: number;
  providerCostUsd?: number;
}): number {
  if ((args.providerCostUsd ?? 0) > 0) {
    return mediaCreditsFromProviderUsd(args.providerCostUsd as number);
  }
  const sellTtd = imageSellPriceTtd(args);
  return Math.max(1, Math.round(sellTtd / CREDIT_PRICE_TTD));
}

export function videoDurationSeconds(
  durationSeconds?: number,
  videoModel?: VideoPricingModel,
): number {
  const max =
    videoModel === "seedance-2.0"
      ? SEEDANCE_20_MAX_DURATION_SECONDS
      : SEEDANCE_25_MAX_DURATION_SECONDS;
  return Math.max(4, Math.min(max, Math.ceil(Number(durationSeconds) || 4)));
}

/** @deprecated Prefer videoDurationSeconds — blocks remain for callers that still ceil by 5s. */
export function videoDurationBlocks(durationSeconds?: number): number {
  return Math.ceil(videoDurationSeconds(durationSeconds) / 5);
}

export function normalizeVideoResolutionKey(
  resolution: string | undefined,
): "854x480" | "1280x720" | "1920x1080" | "3840x2160" {
  const raw = (resolution ?? "1280x720").toLowerCase().replace(/×/g, "x");
  if (
    raw === "3840x2160" ||
    raw === "2160x3840" ||
    raw === "4k" ||
    raw === "2160p" ||
    raw.includes("3840") ||
    raw.includes("2160")
  ) {
    return "3840x2160";
  }
  if (
    raw === "854x480" ||
    raw === "864x480" ||
    raw === "480p" ||
    raw === "480" ||
    (raw.includes("480") && !raw.includes("1480"))
  ) {
    return "854x480";
  }
  if (raw === "1920x1080" || raw === "1080p" || raw.includes("1080")) {
    return "1920x1080";
  }
  return "1280x720";
}

function seedance20UsdPerMTokens(
  resolutionKey: ReturnType<typeof normalizeVideoResolutionKey>,
  withVideoInput: boolean,
): number {
  if (withVideoInput) {
    if (resolutionKey === "1920x1080") {
      return SEEDANCE_20_USD_PER_M_TOKENS_1080P_WITH_VIDEO;
    }
    if (resolutionKey === "3840x2160") {
      return SEEDANCE_20_USD_PER_M_TOKENS_4K_WITH_VIDEO;
    }
    return SEEDANCE_20_USD_PER_M_TOKENS_WITH_VIDEO;
  }
  if (resolutionKey === "1920x1080") return SEEDANCE_20_USD_PER_M_TOKENS_1080P;
  if (resolutionKey === "3840x2160") return SEEDANCE_20_USD_PER_M_TOKENS_4K;
  return SEEDANCE_20_USD_PER_M_TOKENS_NO_VIDEO;
}

export function seedanceOutputTokens(args: {
  resolution?: string;
  durationSeconds?: number;
  videoModel?: VideoPricingModel;
}): number {
  let key = normalizeVideoResolutionKey(args.resolution);
  // Seedance 2.5 has no 4K tier. Price a stray 4K request as 1080p.
  if (
    (args.videoModel === "seedance-2.5" || args.videoModel == null) &&
    key === "3840x2160"
  ) {
    key = "1920x1080";
  }
  const { width, height } = VIDEO_RESOLUTION_WH[key];
  const seconds = videoDurationSeconds(args.durationSeconds, args.videoModel);
  return (height * width * SEEDANCE_FPS * seconds) / 1024;
}

export function seedanceUsdPerMTokens(args: {
  resolution?: string;
  hasVideoReferenceInput?: boolean;
  videoModel?: VideoPricingModel;
}): number {
  const videoModel = args.videoModel ?? "seedance-2.5";
  const withVideoInput = Boolean(args.hasVideoReferenceInput);
  let pricedKey = normalizeVideoResolutionKey(args.resolution);
  if (videoModel === "seedance-2.5" && pricedKey === "3840x2160") {
    pricedKey = "1920x1080";
  }
  if (videoModel === "seedance-2.0") {
    return seedance20UsdPerMTokens(pricedKey, withVideoInput);
  }
  if (pricedKey === "1920x1080") {
    return withVideoInput
      ? SEEDANCE_25_USD_PER_M_TOKENS_1080P_WITH_VIDEO
      : SEEDANCE_25_USD_PER_M_TOKENS_1080P;
  }
  return withVideoInput
    ? SEEDANCE_USD_PER_M_TOKENS_WITH_VIDEO
    : SEEDANCE_USD_PER_M_TOKENS_NO_VIDEO;
}

/** Provider USD from Seedance `usage.completion_tokens` × list $/M. */
export function videoUsdFromCompletionTokens(args: {
  completionTokens: number;
  resolution?: string;
  hasVideoReferenceInput?: boolean;
  videoModel?: VideoPricingModel;
}): number {
  const tokens = Math.max(0, Number(args.completionTokens) || 0);
  return (tokens * seedanceUsdPerMTokens(args)) / 1_000_000;
}

export function estimateVideoModelUsd(args: {
  resolution?: string;
  durationSeconds?: number;
  hasVideoReferenceInput?: boolean;
  audioEnabled?: boolean;
  videoModel?: VideoPricingModel;
}): number {
  // Audio is included in Seedance list rates (no separate surcharge).
  void args.audioEnabled;
  return videoUsdFromCompletionTokens({
    completionTokens: seedanceOutputTokens({
      resolution: args.resolution,
      durationSeconds: args.durationSeconds,
      videoModel: args.videoModel,
    }),
    resolution: args.resolution,
    hasVideoReferenceInput: args.hasVideoReferenceInput,
    videoModel: args.videoModel,
  });
}

export function videoSellPriceTtd(args: {
  resolution?: string;
  durationSeconds?: number;
  hasVideoReferenceInput?: boolean;
  audioEnabled?: boolean;
  videoModel?: VideoPricingModel;
}): number {
  return roundUpToHalfTtd(estimateVideoModelUsd(args) * USD_TO_TTD * 2);
}

/**
 * @deprecated Prefer videoCreditCost — returns 5s silent Seedance/Kling base
 * credits for a resolution key.
 */
export function videoBaseCreditsPerBlock(
  resolution: string | undefined,
  videoModel: VideoPricingModel = "seedance-2.5",
): number {
  return videoCreditCost({
    resolution,
    durationSeconds: 5,
    videoModel,
    audioEnabled: false,
    hasVideoReferenceInput: false,
  });
}

export function videoCreditCost(args: {
  resolution?: string;
  durationSeconds?: number;
  hasReferenceInput?: boolean;
  hasVideoReferenceInput?: boolean;
  hasNonVideoReferenceInput?: boolean;
  audioEnabled?: boolean;
  videoModel?: VideoPricingModel;
  providerCostUsd?: number;
  completionTokens?: number;
}): number {
  // Image / audio refs do not change Seedance list $/M (only video input does).
  void args.hasReferenceInput;
  void args.hasNonVideoReferenceInput;
  if ((args.providerCostUsd ?? 0) > 0) {
    return mediaCreditsFromProviderUsd(args.providerCostUsd as number);
  }
  if ((args.completionTokens ?? 0) > 0) {
    return mediaCreditsFromProviderUsd(
      videoUsdFromCompletionTokens({
        completionTokens: args.completionTokens as number,
        resolution: args.resolution,
        hasVideoReferenceInput: args.hasVideoReferenceInput,
        videoModel: args.videoModel,
      }),
    );
  }

  const sellTtd = videoSellPriceTtd({
    resolution: args.resolution,
    durationSeconds: args.durationSeconds,
    hasVideoReferenceInput: args.hasVideoReferenceInput,
    audioEnabled: args.audioEnabled,
    videoModel: args.videoModel,
  });
  return Math.max(1, Math.round(sellTtd / CREDIT_PRICE_TTD));
}

/**
 * Ledger credits from the provider invoice (or Seedance completion tokens).
 * Quotes still use the estimate tables; this is only for settle/true-up.
 */
export function mediaActualCredits(args: {
  providerCostUsd?: number;
  completionTokens?: number;
  resolution?: string;
  hasVideoReferenceInput?: boolean;
  videoModel?: VideoPricingModel;
}): number | undefined {
  if ((args.providerCostUsd ?? 0) > 0) {
    return mediaCreditsFromProviderUsd(args.providerCostUsd as number);
  }
  if ((args.completionTokens ?? 0) > 0) {
    return mediaCreditsFromProviderUsd(
      videoUsdFromCompletionTokens({
        completionTokens: args.completionTokens as number,
        resolution: args.resolution,
        hasVideoReferenceInput: args.hasVideoReferenceInput,
        videoModel: args.videoModel,
      }),
    );
  }
  return undefined;
}

/**
 * Over-quote above this many credits signals a pricing bug (alert). A 1-credit
 * overrun is the normal TT$0.50 rounding edge (e.g. 720p/4s) — logged, not alerted.
 */
export const SETTLE_ALERT_OVER_QUOTE_CREDITS = 1;

export type QuoteCappedSettlement = {
  /** Credits the customer finally pays: min(actual, quote). */
  chargedCredits: number;
  /** Credits returned from the hold (quote − charged), never negative. */
  refundedCredits: number;
  /** Provider-measured credits above the quote that were NOT billed. */
  unbilledOverQuoteCredits: number;
  /** True when the over-quote is > SETTLE_ALERT_OVER_QUOTE_CREDITS (pricing bug). */
  alert: boolean;
};

/**
 * Cap-at-quote settle rule. The customer never pays more than the quote they
 * saw; when the measured cost is lower, the difference goes back. Excess over
 * the quote is absorbed and reported (alert only when > 1 credit).
 */
export function settleChargeAtQuote(args: {
  quotedCredits: number;
  actualCredits?: number;
}): QuoteCappedSettlement {
  const quoted = Math.max(0, Number(args.quotedCredits) || 0);
  const actualRaw = args.actualCredits;
  if (actualRaw == null || !Number.isFinite(actualRaw) || actualRaw <= 0) {
    return { chargedCredits: quoted, refundedCredits: 0, unbilledOverQuoteCredits: 0, alert: false };
  }
  const actual = Math.max(0, actualRaw);
  const charged = Math.min(actual, quoted);
  const over = Math.max(0, actual - quoted);
  const alert = over > SETTLE_ALERT_OVER_QUOTE_CREDITS;
  return {
    chargedCredits: charged,
    refundedCredits: Math.max(0, quoted - charged),
    unbilledOverQuoteCredits: over,
    alert,
  };
}

export function estimateTextModelUsd(args: {
  imageReferenceCount?: number;
  videoReferenceCount?: number;
  audioReferenceCount?: number;
  textModel?: TextPricingModel;
}): number {
  const rates = textRates(args.textModel ?? "glimmer");
  const imageRefs = Math.max(0, Math.ceil(args.imageReferenceCount ?? 0));
  const videoRefs = Math.max(0, Math.ceil(args.videoReferenceCount ?? 0));
  const audioRefs = Math.max(0, Math.ceil(args.audioReferenceCount ?? 0));

  const textImageVideoInputTokens =
    TEXT_BASE_INPUT_TOKENS +
    imageRefs * TEXT_IMAGE_REF_INPUT_TOKENS +
    videoRefs * TEXT_VIDEO_REF_INPUT_TOKENS;
  const audioInputTokens = audioRefs * TEXT_AUDIO_REF_INPUT_TOKENS;

  return (
    (textImageVideoInputTokens * rates.input) / 1_000_000 +
    (audioInputTokens * rates.audioInput) / 1_000_000 +
    (TEXT_BASE_OUTPUT_TOKENS * rates.output) / 1_000_000
  );
}

export type MeasuredTextUsage = {
  /** Non-cached prompt tokens (never include cache hits). */
  inputTokens?: number;
  outputTokens?: number;
  /** BytePlus cache-hit input tokens. */
  cacheReadTokens?: number;
  /**
   * Tokens written into a new cache. BytePlus bills storage (USD/M/hour), not
   * Anthropic-style write×input. When hours unknown, bill 1h min TTL.
   */
  cacheWriteTokens?: number;
  /** Optional storage duration override (hours). Default TEXT_CACHE_STORAGE_ASSUMED_HOURS. */
  cacheStorageHours?: number;
  /**
   * Optional total prompt_tokens from the provider. When set, non-cached input is
   * derived as prompt − cacheRead − cacheWrite (prevents double-billing).
   */
  promptTokens?: number;
  /**
   * USD the provider billed us for this usage (OpenRouter `usage.cost`, Pi
   * session cost, etc.). When set, sell price is this ×2 — not a rate table.
   */
  providerCostUsd?: number;
  /**
   * Largest single request prompt (input + cache) inside summed multi-step
   * usage. Long-prompt tiers (Haiku 5.5 over 100K) are per request.
   */
  maxRequestPromptTokens?: number;
};

function finiteNonNegInt(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

function finiteNonNeg(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/** Normalize measured usage so input never double-counts cache buckets. */
export function normalizeMeasuredTextUsage(
  usage: MeasuredTextUsage,
): MeasuredTextUsage {
  const cacheReadTokens = finiteNonNegInt(usage.cacheReadTokens);
  const cacheWriteTokens = finiteNonNegInt(usage.cacheWriteTokens);
  const outputTokens = finiteNonNegInt(usage.outputTokens);
  const promptTokens = finiteNonNegInt(usage.promptTokens);
  let inputTokens = finiteNonNegInt(usage.inputTokens);
  if (promptTokens > 0) {
    inputTokens = Math.max(0, promptTokens - cacheReadTokens - cacheWriteTokens);
  }
  const cacheStorageHours =
    usage.cacheStorageHours != null && Number.isFinite(usage.cacheStorageHours)
      ? Math.max(0, Number(usage.cacheStorageHours))
      : TEXT_CACHE_STORAGE_ASSUMED_HOURS;
  const providerCostUsd = finiteNonNeg(usage.providerCostUsd);
  return {
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheWriteTokens,
    cacheStorageHours,
    ...(promptTokens > 0 ? { promptTokens } : {}),
    ...(providerCostUsd > 0 ? { providerCostUsd } : {}),
    ...(finiteNonNegInt(usage.maxRequestPromptTokens) > 0
      ? { maxRequestPromptTokens: finiteNonNegInt(usage.maxRequestPromptTokens) }
      : {}),
  };
}

/** Provider USD: actual invoice when present, else list rate × measured tokens. */
export function textProviderCostUsd(
  usage: MeasuredTextUsage,
  textModel: TextPricingModel = "glimmer",
): number {
  const n = normalizeMeasuredTextUsage(usage);
  if ((n.providerCostUsd ?? 0) > 0) return n.providerCostUsd as number;
  const inputTokens = n.inputTokens ?? 0;
  const outputTokens = n.outputTokens ?? 0;
  const cacheReadTokens = n.cacheReadTokens ?? 0;
  const cacheWriteTokens = n.cacheWriteTokens ?? 0;
  const rates = { ...textRates(textModel) };
  // Haiku 5.5: a prompt over 100K tokens (input + cache read + cache write)
  // bills the whole request at the long-prompt list.
  // A multi-step agent slice reports summed usage; it passes the largest single
  // request as maxRequestPromptTokens, and the tier follows that request.
  const longPromptBasis =
    (n.maxRequestPromptTokens ?? 0) > 0
      ? (n.maxRequestPromptTokens as number)
      : inputTokens + cacheReadTokens + cacheWriteTokens;
  if (textModel === "haiku55" && longPromptBasis > TEXT_HAIKU55_LONG_PROMPT_TOKENS) {
    rates.input = TEXT_HAIKU55_LONG_USD_PER_M_INPUT;
    rates.output = TEXT_HAIKU55_LONG_USD_PER_M_OUTPUT;
    rates.cacheRead = TEXT_HAIKU55_LONG_USD_PER_M_CACHE_READ;
  }
  const cacheStorageHours = n.cacheStorageHours ?? TEXT_CACHE_STORAGE_ASSUMED_HOURS;
  const microUsd =
    inputTokens * rates.input +
    cacheReadTokens * rates.cacheRead +
    cacheWriteTokens * rates.cacheStorage * cacheStorageHours +
    outputTokens * rates.output;
  return microUsd / 1_000_000;
}

/** Customer credits from the USD the provider billed us, ×2, media half-dollar. */
export function mediaCreditsFromProviderUsd(providerCostUsd: number): number {
  if (!Number.isFinite(providerCostUsd) || providerCostUsd <= 0) return 0;
  const sellTtd = roundUpToHalfTtd(providerCostUsd * USD_TO_TTD * 2);
  return Math.max(1, Math.round(sellTtd / CREDIT_PRICE_TTD));
}

/**
 * Customer TT$ = provider COGS ×2, then text sell rounding
 * (1¢ floor for sub-cent; else round up to next TT$0.05).
 */
export function textSellPriceFromUsageTtd(
  usage: MeasuredTextUsage,
  textModel: TextPricingModel = "glimmer",
): number {
  if (textModel === "union") return 0;
  const raw = textProviderCostUsd(usage, textModel) * USD_TO_TTD * 2;
  return roundTextSellTtd(raw);
}

export function textCreditsFromMeasuredUsage(
  usage: MeasuredTextUsage,
  textModel: TextPricingModel = "glimmer",
): number {
  const sellTtd = textSellPriceFromUsageTtd(usage, textModel);
  // Fractional credits OK (TT$0.05 = 0.1 credit at TT$0.50/credit).
  return Math.round((sellTtd / CREDIT_PRICE_TTD) * 100) / 100;
}

export function formatTextUsageReason(
  usage: MeasuredTextUsage,
  textModel: TextPricingModel = "glimmer",
): string {
  const n = normalizeMeasuredTextUsage(usage);
  const extraTierLabels: Partial<Record<TextPricingModel, string>> = {
    luna56: "GPT Luna 5.6",
    haiku55: "Claude Haiku 5.5",
    terra: "GPT Terra 5.6",
    "deepseek-flash": "DeepSeek V4.1 Flash",
    "qwen-flash": "Qwen 3.8 Flash",
    "gemini-premium": "Gemini 3.5 Flash",
    "gemini-lite": "Gemini Flash Lite",
    "gemini-mini": "Gemini 2.5 Flash Lite",
    "gpt5-mini": "GPT-5 Mini",
    premium: "Premium (unlisted model)",
  };
  const tier =
    extraTierLabels[textModel] ??
    (textModel === "glimmer"
      ? "Glimmer"
      : textModel === "flash" || textModel === "spark"
        ? "Spark"
        : textModel === "gemini"
          ? "Gemini 3.8 Flash"
          : textModel === "luna"
            ? "GPT Luna"
            : textModel === "mercury"
            ? "Mercury 2.5"
            : textModel === "sol"
            ? "GPT-6 Sol Pro"
            : textModel === "union"
              ? "Union Alpha"
              : textModel === "glm"
              ? "GLM 5.3"
              : textModel === "glm-flash"
                ? "GLM 5.3 Flash"
        : textModel === "mini"
          ? "Seed Mini"
          : textModel === "lite"
            ? "Seed Lite"
            : textModel === "pro"
              ? "Seed Pro"
              : "Seed Turbo");
  return (
    `Text (${tier}) in=${n.inputTokens ?? 0} cacheHit=${n.cacheReadTokens ?? 0}` +
    ` cacheStore1h=${n.cacheWriteTokens ?? 0} out=${n.outputTokens ?? 0}` +
    (n.providerCostUsd != null && n.providerCostUsd > 0
      ? ` cogsUsd=${n.providerCostUsd.toFixed(6)}×2`
      : "")
  );
}

export function addMeasuredTextUsage(
  left: MeasuredTextUsage,
  right: MeasuredTextUsage,
): MeasuredTextUsage {
  const providerCostUsd =
    (left.providerCostUsd ?? 0) + (right.providerCostUsd ?? 0);
  return {
    inputTokens: (left.inputTokens ?? 0) + (right.inputTokens ?? 0),
    outputTokens: (left.outputTokens ?? 0) + (right.outputTokens ?? 0),
    cacheReadTokens: (left.cacheReadTokens ?? 0) + (right.cacheReadTokens ?? 0),
    cacheWriteTokens: (left.cacheWriteTokens ?? 0) + (right.cacheWriteTokens ?? 0),
    ...(providerCostUsd > 0 ? { providerCostUsd } : {}),
  };
}

export function measuredTextUsageFromGateway(usage: {
  inputTokens?: number | undefined;
  outputTokens?: number | undefined;
  cacheReadTokens?: number | undefined;
  cacheWriteTokens?: number | undefined;
  promptTokens?: number | undefined;
  providerCostUsd?: number | undefined;
}): MeasuredTextUsage {
  return normalizeMeasuredTextUsage({
    inputTokens: usage.inputTokens ?? 0,
    outputTokens: usage.outputTokens ?? 0,
    cacheReadTokens: usage.cacheReadTokens ?? 0,
    cacheWriteTokens: usage.cacheWriteTokens ?? 0,
    promptTokens: usage.promptTokens,
    providerCostUsd: usage.providerCostUsd,
  });
}

/** Customer TT$ for script / Assistance estimates = COGS ×2, text sell rounding. */
export function textSellPriceTtd(args: {
  imageReferenceCount?: number;
  videoReferenceCount?: number;
  audioReferenceCount?: number;
  textModel?: TextPricingModel;
}): number {
  const raw = estimateTextModelUsd(args) * USD_TO_TTD * 2;
  return roundTextSellTtd(raw);
}

export function textCreditCost(args: {
  imageReferenceCount?: number;
  videoReferenceCount?: number;
  audioReferenceCount?: number;
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  providerCostUsd?: number;
  maxRequestPromptTokens?: number;
  textModel?: TextPricingModel;
}): number {
  if (
    args.providerCostUsd != null ||
    args.inputTokens != null ||
    args.outputTokens != null ||
    args.cacheReadTokens != null ||
    args.cacheWriteTokens != null
  ) {
    return textCreditsFromMeasuredUsage(
      {
        inputTokens: args.inputTokens,
        outputTokens: args.outputTokens,
        cacheReadTokens: args.cacheReadTokens,
        cacheWriteTokens: args.cacheWriteTokens,
        providerCostUsd: args.providerCostUsd,
        ...(args.maxRequestPromptTokens ? { maxRequestPromptTokens: args.maxRequestPromptTokens } : {}),
      },
      args.textModel ?? "glimmer",
    );
  }
  const sellTtd = textSellPriceTtd(args);
  return Math.max(
    TEXT_MIN_SELL_TTD / CREDIT_PRICE_TTD,
    Math.round((sellTtd / CREDIT_PRICE_TTD) * 100) / 100,
  );
}

export type GenerationCreditTier =
  | "image"
  | "pro_video"
  | "audio"
  | "low"
  | "medium"
  | "high";

export type AudioGenType = "voiceover" | "sfx" | "music";

/**
 * ElevenLabs TTS — USD per 1K characters.
 *
 * 2026-09-29: the rate card lists $0.08 for both v3 and v4 (the v4 launch
 * promo of $0.022 ends 2026-10-12, so it is deliberately NOT baked in — COGS
 * must hold at full rate). This constant previously read 0.1, overstating
 * cost by 25% and inflating the 2x sell price.
 */
export const ELEVEN_V4_USD_PER_1K_CHARS = 0.08;
/** ElevenLabs Sound Effects — USD per minute of output. */
export const ELEVEN_SFX_USD_PER_MINUTE = 0.12;
/** Default estimate length when SFX duration is Auto. */
export const ELEVEN_SFX_AUTO_DURATION_SECONDS = 5;
/** Eleven Music — USD per minute of output (COGS; sell is 2× via audioSellPriceTtd). */
export const ELEVEN_MUSIC_USD_PER_MINUTE = 0.15;
/** Default music length when duration is omitted (ads). */
export const ELEVEN_MUSIC_DEFAULT_DURATION_SECONDS = 30;
/** Self-serve Music API billable range (3s–10min). */
export const ELEVEN_MUSIC_MIN_DURATION_SECONDS = 3;
export const ELEVEN_MUSIC_MAX_DURATION_SECONDS = 600;

export function estimateVoiceoverUsd(characterCount: number): number {
  const chars = Math.max(0, Math.ceil(Number(characterCount) || 0));
  return (chars / 1000) * ELEVEN_V4_USD_PER_1K_CHARS;
}

export function estimateSfxUsd(durationSeconds?: number | null): number {
  const seconds =
    durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds <= 0
      ? ELEVEN_SFX_AUTO_DURATION_SECONDS
      : Math.max(0.5, Math.min(30, Number(durationSeconds)));
  return (seconds / 60) * ELEVEN_SFX_USD_PER_MINUTE;
}

export function estimateMusicUsd(durationSeconds?: number | null): number {
  const seconds =
    durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds <= 0
      ? ELEVEN_MUSIC_DEFAULT_DURATION_SECONDS
      : Math.max(
          ELEVEN_MUSIC_MIN_DURATION_SECONDS,
          Math.min(ELEVEN_MUSIC_MAX_DURATION_SECONDS, Number(durationSeconds)),
        );
  return (seconds / 60) * ELEVEN_MUSIC_USD_PER_MINUTE;
}

export function audioSellPriceTtd(args: {
  audioType: AudioGenType;
  characterCount?: number;
  durationSeconds?: number | null;
}): number {
  const usd =
    args.audioType === "voiceover"
      ? estimateVoiceoverUsd(args.characterCount ?? 0)
      : args.audioType === "sfx"
        ? estimateSfxUsd(args.durationSeconds)
        : estimateMusicUsd(args.durationSeconds);
  return roundUpToHalfTtd(usd * USD_TO_TTD * 2);
}

export function audioCreditCost(args: {
  audioType: AudioGenType;
  characterCount?: number;
  durationSeconds?: number | null;
}): number {
  const sellTtd = audioSellPriceTtd(args);
  return Math.max(1, Math.round(sellTtd / CREDIT_PRICE_TTD));
}

export function creditCostForGeneration(args: {
  tier: GenerationCreditTier;
  resolution?: string;
  quality?: string;
  aspectRatio?: string;
  durationSeconds?: number;
  hasReferenceInput?: boolean;
  referenceImageCount?: number;
  hasVideoReferenceInput?: boolean;
  hasNonVideoReferenceInput?: boolean;
  audioEnabled?: boolean;
  videoModel?: VideoPricingModel | string;
  imageModel?: string;
  audioType?: AudioGenType;
  characterCount?: number;
}): number {
  const hinted =
    mediaModelById(typeof args.videoModel === "string" ? args.videoModel : undefined) ??
    mediaModelById(typeof args.imageModel === "string" ? args.imageModel : undefined);
  if (hinted?.provider === "higgsfield") return hinted.quoteCredits;
  if (args.tier === "pro_video") {
    const slug = String(args.videoModel ?? "");
    const videoModel =
      slug.includes("2.5") || slug.includes("2-5") || !slug.includes("seedance")
        ? "seedance-2.5"
        : "seedance-2.0";
    return videoCreditCost({ ...args, videoModel });
  }
  if (args.tier === "audio") {
    return audioCreditCost({
      audioType: args.audioType ?? "voiceover",
      characterCount: args.characterCount,
      durationSeconds: args.durationSeconds,
    });
  }
  return imageCreditCost({
    resolution: args.resolution,
    quality: args.quality,
    aspectRatio: args.aspectRatio,
    hasReferenceInput: args.hasReferenceInput,
    referenceImageCount: args.referenceImageCount,
  });
}

/**
 * Live quote for DM generation proposal cards / updateGenerationProposal.
 * Unit price matches Create + DM generate (`creditCostForGeneration`); image
 * count multiplies the unit quote.
 */
export function proposalGenerationCreditCost(args: {
  mediaKind: "image" | "video" | "audio";
  resolution?: string;
  quality?: string;
  aspectRatio?: string;
  durationSeconds?: number;
  count?: number;
  videoModel?: string;
  imageModel?: string;
  audioType?: AudioGenType;
  characterCount?: number;
  hasVideoReferenceInput?: boolean;
  audioEnabled?: boolean;
}): number {
  const count = Math.max(1, Math.min(4, Math.round(Number(args.count) || 1)));
  const unit =
    args.mediaKind === "audio"
      ? audioCreditCost({
          audioType: args.audioType ?? "voiceover",
          characterCount: args.characterCount,
          durationSeconds: args.durationSeconds,
        })
      : creditCostForGeneration({
          tier: args.mediaKind === "video" ? "pro_video" : "image",
          resolution: args.resolution,
          quality: args.mediaKind === "image" ? (args.quality ?? "medium") : undefined,
          aspectRatio: args.mediaKind === "image" ? args.aspectRatio : undefined,
          durationSeconds:
            args.mediaKind === "video"
              ? Math.max(4, Number(args.durationSeconds) || 4)
              : undefined,
          videoModel:
            args.mediaKind === "video"
              ? (args.videoModel ?? "seedance-2.5")
              : undefined,
          imageModel: args.mediaKind === "image" ? args.imageModel : undefined,
          hasVideoReferenceInput:
            args.mediaKind === "video" ? args.hasVideoReferenceInput : undefined,
          audioEnabled: args.mediaKind === "video" ? args.audioEnabled : undefined,
        });
  return Math.max(1, unit * count);
}

/**
 * Deepgram nova-3 pre-recorded (pay-as-you-go list, 2026): US$0.0077 / minute.
 * Customer SKU = 2× COGS, FX US$1 = TT$10, round **total** up to TT$0.50,
 * 1 credit = TT$0.50, floor 1 credit. Recorder pays.
 *
 * Billing is Twilio-style: charge per minute as you go from the full balance
 * (no hold, no backup floor).
 */
export const DEEPGRAM_NOVA3_PRE_RECORDED_USD_PER_MIN = 0.0077;
/** @deprecated Soft quote only — drip bills per minute from balance. */
export const CALL_TRANSCRIPT_RESERVE_CAP_MINUTES = 30;
/** @deprecated No backup floor — Twilio drip uses the full balance. */
export const CALL_TRANSCRIPT_BALANCE_BACKUP_CREDITS = 0;

export function callTranscriptCreditsForMinutes(minutes: number): number {
  const mins = Math.max(0, Number(minutes) || 0);
  if (mins <= 0) return 0;
  const usd = DEEPGRAM_NOVA3_PRE_RECORDED_USD_PER_MIN * mins;
  const ttd = roundUpToHalfTtd(usd * USD_TO_TTD * 2);
  return Math.max(1, Math.round(ttd / CREDIT_PRICE_TTD));
}

export function callTranscriptCreditsPerMinuteQuote(): number {
  return callTranscriptCreditsForMinutes(1);
}

/** Balance available for recording (full wallet — Twilio drip). */
export function callTranscriptSpendableCredits(balance: number): number {
  return Math.max(0, Number(balance) || 0);
}

/** Whole minutes the wallet can cover at the per-minute quote. */
export function callTranscriptMaxMinutesForBalance(balance: number): number {
  const perMin = callTranscriptCreditsPerMinuteQuote();
  const spendable = callTranscriptSpendableCredits(balance);
  if (perMin <= 0 || spendable < perMin) return 0;
  return Math.floor(spendable / perMin);
}

/** @deprecated Prefer {@link callTranscriptMaxMinutesForBalance} / drip billing. */
export function callTranscriptReserveCreditsForBalance(balance: number): number {
  const perMin = callTranscriptCreditsPerMinuteQuote();
  return callTranscriptMaxMinutesForBalance(balance) * perMin;
}

/** @deprecated Prefer {@link callTranscriptMaxMinutesForBalance}. */
export function callTranscriptReserveCredits(): number {
  return callTranscriptCreditsForMinutes(CALL_TRANSCRIPT_RESERVE_CAP_MINUTES);
}

/** @deprecated Always 0 — no backup floor. */
export function callTranscriptBackupCredits(): number {
  return 0;
}
